import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CONSULTATION_PHOTOS = ['/gallery-images/consult1.jpg', '/gallery-images/consult2.jpg', '/gallery-images/consult3.jpg'];
const DAYCARE_PHOTOS = ['/gallery-images/daycare1.jpg', '/gallery-images/daycare2.jpg'];

const buildFeatureSections = (t) => [
  {
    image: '/gallery-images/hero_entrance.jpg',
    title: t('facility.entrance.title', 'Clinic Entrance'),
    subtitle: t('facility.entrance.subtitle', 'Tonge Healthcare'),
    caption: t('facility.entrance.caption', 'Professional healthcare facility with modern infrastructure and easy accessibility'),
    badge: t('facility.location', '📍 Chandrapur, Maharashtra')
  },
  {
    image: '/gallery-images/building.jpg',
    title: t('facility.building.title', 'Our Building'),
    subtitle: t('facility.building.subtitle', 'Located in Chandrapur'),
    caption: t('facility.building.caption', 'Complete view of our healthcare facility serving the community'),
    reverse: true
  },
  {
    image: '/gallery-images/waiting.jpg',
    title: t('facility.waiting.title', 'Waiting Area'),
    subtitle: t('facility.waiting.subtitle', 'Comfortable patient space'),
    caption: t('facility.waiting.caption', 'Modern waiting area with comfortable seating and clean, welcoming interiors')
  },
  {
    image: '/gallery-images/reception.jpg',
    title: t('facility.reception.title', 'Reception Desk'),
    subtitle: t('facility.reception.subtitle', 'First point of care'),
    caption: t('facility.reception.caption', 'Modern reception desk with professional staff seating and patient-friendly design'),
    reverse: true
  }
];

const FACILITY_STYLES = `
  .facility-section {
    opacity: 0;
    transform: translateY(60px);
    transition: opacity 1.2s ease-out, transform 1.2s ease-out;
  }
  .facility-visible {
    opacity: 1;
    transform: translateY(0);
  }
  .facility-image {
    opacity: 0;
    transform: scale(0.95);
    transition: opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s;
  }
  .facility-visible .facility-image {
    opacity: 1;
    transform: scale(1);
  }
  .facility-content {
    opacity: 0;
    transform: translateX(-30px);
    transition: opacity 1s ease-out 0.4s, transform 1s ease-out 0.4s;
  }
  .facility-visible .facility-content {
    opacity: 1;
    transform: translateX(0);
  }
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fade-in 1s ease-out;
  }
  .animate-fade-in-delay {
    animation: fade-in 1s ease-out 0.3s both;
  }
  @media (max-width: 768px) {
    .facility-content {
      transform: translateY(-20px);
    }
    .facility-visible .facility-content {
      transform: translateY(0);
    }
  }
`;

const FeatureSection = ({ innerRef, image, alt, title, subtitle, caption, reverse = false, badge = null, bg = 'bg-white' }) => (
  <section ref={innerRef} className={`facility-section min-h-screen flex items-center justify-center py-20 px-4 ${bg}`}>
    <div className="max-w-7xl mx-auto w-full">
      <div className={`grid md:grid-cols-5 gap-12 items-center ${reverse ? 'md:grid-flow-dense' : ''}`}>
        <div className={`facility-image md:col-span-3 ${reverse ? 'md:col-start-3' : ''}`}>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-100">
            <img
              src={image}
              alt={alt}
              className="w-full h-auto object-contain transform transition-transform duration-700 hover:scale-105"
              loading="lazy"
              style={{ maxHeight: '70vh' }}
            />
          </div>
        </div>
        <div className={`facility-content space-y-6 md:col-span-2 ${reverse ? 'md:col-start-1 md:row-start-1' : ''}`}>
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">{title}</h2>
            <p className="text-xl text-emerald-700 font-medium">{subtitle}</p>
            <p className="text-lg text-slate-600 leading-relaxed">{caption}</p>
          </div>
          {badge && (
            <div className="pt-4">
              <div className="inline-block px-6 py-3 bg-emerald-50 rounded-full">
                <p className="text-emerald-800 font-semibold">{badge}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
);

const PhotoGridSection = ({ innerRef, title, subtitle, caption, photos, altPrefix, bg = 'bg-white' }) => (
  <section ref={innerRef} className={`facility-section min-h-screen flex items-center justify-center py-20 px-4 ${bg}`}>
    <div className="max-w-7xl mx-auto w-full">
      <div className="text-center mb-16 space-y-6">
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900">{title}</h2>
        <p className="text-xl text-emerald-700 font-medium">{subtitle}</p>
        <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">{caption}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {photos.map((photoUrl, idx) => (
          <div key={photoUrl} className="facility-image">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-100">
              <img
                src={photoUrl}
                alt={`${altPrefix} view ${idx + 1}`}
                className="w-full h-auto object-contain transform transition-transform duration-700 hover:scale-105"
                loading="lazy"
                style={{ maxHeight: '60vh' }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GalleryHero = ({ onBack, title, subtitle, backLabel }) => (
  <section className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-60"
      style={{
        backgroundImage: `url(/gallery-images/hero_entrance.jpg)`,
        backgroundAttachment: 'fixed'
      }}
    />
    <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
      <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">{title}</h1>
      <p className="text-xl md:text-2xl text-gray-200 mb-8 animate-fade-in-delay">{subtitle}</p>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md text-white rounded-full hover:bg-white/20 transition-all duration-300"
        data-testid="gallery-back-home"
      >
        <ArrowLeft size={20} />
        {backLabel}
      </button>
    </div>
  </section>
);

const GalleryCTA = ({ title, description, bookLabel, directionsLabel, navigate }) => (
  <section className="py-24 px-4 bg-gradient-to-br from-emerald-50 via-white to-slate-50">
    <div className="max-w-4xl mx-auto text-center space-y-8">
      <h2 className="text-4xl md:text-5xl font-bold text-slate-900">{title}</h2>
      <p className="text-xl text-slate-600">{description}</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={() => navigate('/')}
          className="px-8 py-4 text-white rounded-full font-semibold hover:bg-emerald-800 transition-all duration-300 shadow-lg hover:shadow-xl"
          style={{ backgroundColor: 'var(--medical-primary)' }}
          data-testid="gallery-cta-book"
        >
          {bookLabel}
        </button>
        <button
          onClick={() => navigate('/#location')}
          className="px-8 py-4 bg-white text-emerald-700 rounded-full font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-emerald-700"
          data-testid="gallery-cta-directions"
        >
          {directionsLabel}
        </button>
      </div>
    </div>
  </section>
);

const FacilityPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sectionsRef = useRef([]);
  let sectionIdx = 0;
  const nextRef = () => {
    const i = sectionIdx++;
    return (el) => (sectionsRef.current[i] = el);
  };

  const featureSections = buildFeatureSections(t);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('facility-visible');
        }
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });

    const sections = sectionsRef.current.filter(Boolean);
    sections.forEach((section) => observer.observe(section));
    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <GalleryHero
        onBack={() => navigate('/')}
        title={t('facility.hero.title', 'Gallery')}
        subtitle={t('facility.hero.subtitle', 'Explore Our Modern Healthcare Facility')}
        backLabel={t('facility.backHome', 'Back to Home')}
      />

      {featureSections.map((section) => (
        <FeatureSection key={section.image} innerRef={nextRef()} alt={section.title} {...section} />
      ))}

      <PhotoGridSection
        innerRef={nextRef()}
        bg="bg-slate-50"
        title={t('facility.consultation.title', "Doctor's Consultation Chamber")}
        subtitle={t('facility.consultation.subtitle', 'Professional medical consultation')}
        caption={t('facility.consultation.caption', 'Well-equipped consultation room with complete patient care facilities')}
        photos={CONSULTATION_PHOTOS}
        altPrefix="Consultation area"
      />

      <FeatureSection
        innerRef={nextRef()}
        image="/gallery-images/examination.jpg"
        alt="Examination Room"
        title={t('facility.examination.title', 'Examination Room')}
        subtitle={t('facility.examination.subtitle', 'Fully equipped for thorough medical examinations')}
        caption={t('facility.examination.caption', 'Modern examination room with state-of-the-art medical equipment and comfortable patient examination bed')}
      />

      <FeatureSection
        innerRef={nextRef()}
        bg="bg-slate-50"
        reverse
        image="/gallery-images/treatment.jpg"
        alt="Treatment & IV Therapy Area"
        title={t('facility.treatment.title', 'Treatment & IV Therapy')}
        subtitle={t('facility.treatment.subtitle', 'Advanced medical treatments')}
        caption={t('facility.treatment.caption', 'Fully equipped IV therapy station with medical supplies and monitoring equipment')}
      />

      <PhotoGridSection
        innerRef={nextRef()}
        title={t('facility.daycare.title', 'Daycare Facility')}
        subtitle={t('facility.daycare.subtitle', 'Comfortable patient recovery space')}
        caption={t('facility.daycare.caption', 'Modern daycare beds with IV facilities and patient monitoring systems')}
        photos={DAYCARE_PHOTOS}
        altPrefix="Daycare facility"
      />

      <GalleryCTA
        title={t('facility.cta.title', 'Visit Our Clinic')}
        description={t('facility.cta.description', 'Experience world-class healthcare in a comfortable, modern environment')}
        bookLabel={t('facility.cta.bookAppointment', 'Book an Appointment')}
        directionsLabel={t('facility.cta.getDirections', 'Get Directions')}
        navigate={navigate}
      />

      <Footer />

      <style>{FACILITY_STYLES}</style>
    </div>
  );
};

export default FacilityPage;
